import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Search, Trash2 } from 'lucide-react'
import { useCallback, useRef, useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import {
  CONTACT_KIND_VALUES,
  PARTY_ROLE_VALUES,
  STATE_REGISTRATION_STATUS_VALUES,
  type PartyResponse,
  type PartySummary,
} from '@aeris/contracts'
import {
  isValidDocument,
  normalizeDocument,
  partyDisplayName,
} from '@aeris/party'
import {
  Button,
  ConfirmDialog,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  Field,
  Input,
  Select,
  Textarea,
  notify,
} from '@aeris/ui'

import {
  EMPTY_ADDRESS,
  ufOrNull,
  type AddressValues,
} from '../lib/address-values.ts'
import { fetchSalespeople } from '../lib/api/team.ts'
import {
  ApiError,
  createParty,
  fetchNextPartyCode,
  updateParty,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import {
  fillableWith,
  lookupCnpj,
  mergeCnpjAddress,
  phoneKindOf,
  type CnpjCompany,
} from '../lib/cnpj.ts'
import {
  CONTACT_KIND_LABELS,
  IE_STATUS_LABELS,
  PARTY_ROLE_LABELS,
} from '../lib/party-labels.ts'

import { AddressInputs } from './address-inputs.tsx'
import { PartyDetails } from './party-details.tsx'
import { PartyIdentity } from './party-identity.tsx'

/**
 * Create and edit a party.
 *
 * One form for customers, suppliers and carriers, with the roles as checkboxes.
 * That is the whole point of Master Specification section 10: the shop that
 * buys your parts and sells you its own is one registration holding two roles,
 * and a "novo cliente" form beside a "novo fornecedor" form is what produces
 * two rows for one CNPJ.
 *
 * The client validates the same check digits the server does, because a round
 * trip to be told a digit is wrong is a bad experience at a counter. It is not
 * *instead of* the server check — `@aeris/party` is the one package both sides
 * import, so the two answers cannot drift, and the server validates again
 * regardless.
 *
 * ## O CNPJ preenche o cadastro (F130)
 *
 * *"Quando colocar o CNPJ tem que pegar os dados da Receita e já preencher
 * tudo, e o usuário editar."* Com o CNPJ completo e válido, `lib/cnpj.ts`
 * busca razão social, nome fantasia, endereço, telefones e e-mail, e preenche
 * só o que está vazio. Tudo continua editável; sem internet, o cadastro é o de
 * sempre.
 *
 * O endereço e os contatos passaram a existir no "Novo cadastro" e sobem JUNTO
 * com a pessoa, numa transação só no servidor — antes era preciso salvar e
 * reabrir para adicioná-los, e o que a busca trouxe se perderia no caminho.
 *
 * ## O cadastro rápido (F211)
 *
 * *"Tirar obrigatório o e-mail e o CNPJ, e pôr um modelo pro vendedor não
 * ficar perdendo tempo."* Nada disso era obrigatório — o formulário é que
 * PARECIA: abria como empresa ("CNPJ"), com a situação da IE marcada como
 * obrigatória e os campos fiscais todos à mostra. Agora abre como pessoa
 * física, com o que o balcão tem na mão em cima — código, nome, WhatsApp e
 * vendedor — e o resto recolhido em "Mais dados".
 */

const schema = z
  .object({
    kind: z.enum(['person', 'company']),
    legalName: z.string().trim().min(1, 'Informe o nome').max(200),
    tradeName: z.string().trim().max(200),
    document: z.string().trim().max(20),
    stateRegistration: z.string().trim().max(20),
    stateRegistrationStatus: z.enum(STATE_REGISTRATION_STATUS_VALUES),
    municipalRegistration: z.string().trim().max(20),
    notes: z.string().trim().max(2000),
    /** O vendedor do cliente (F210). Vazio é "sem vendedor". */
    salespersonUserId: z.string(),
    roles: z
      .array(z.enum(PARTY_ROLE_VALUES))
      .min(1, 'Escolha ao menos um papel'),
  })
  // Cross-field, so it has to be a refinement: the same eleven characters are a
  // valid CPF and an invalid CNPJ, and which check applies depends on `kind`.
  .refine(
    (values) =>
      values.document.length === 0 ||
      isValidDocument(values.kind, values.document),
    {
      message: 'Documento inválido — confira os dois dígitos verificadores',
      path: ['document'],
    }
  )

type FormValues = z.infer<typeof schema>

const emptyValues: FormValues = {
  // F211: quem chega ao balcão é, na maioria, gente — o CNPJ fica em "Mais dados".
  kind: 'person',
  legalName: '',
  tradeName: '',
  document: '',
  stateRegistration: '',
  stateRegistrationStatus: 'non_taxpayer',
  municipalRegistration: '',
  notes: '',
  salespersonUserId: '',
  roles: ['customer'],
}

const valuesFrom = (party: PartySummary): FormValues => ({
  kind: party.kind,
  legalName: party.legalName,
  tradeName: party.tradeName ?? '',
  document: party.document ?? '',
  stateRegistration: party.stateRegistration ?? '',
  stateRegistrationStatus: party.stateRegistrationStatus,
  municipalRegistration: party.municipalRegistration ?? '',
  notes: party.notes ?? '',
  salespersonUserId: party.salespersonUserId ?? '',
  roles: party.roles.length > 0 ? [...party.roles] : ['customer'],
})

type ContactDraft = {
  readonly key: number
  readonly kind: (typeof CONTACT_KIND_VALUES)[number]
  readonly value: string
}

type Lookup =
  | { readonly state: 'idle' }
  | { readonly state: 'busy' }
  | { readonly state: 'missed' }
  | { readonly state: 'found'; readonly company: CnpjCompany }

/** O mesmo telefone digitado de dois jeitos é um contato só. */
const contactKey = (contact: Pick<ContactDraft, 'kind' | 'value'>): string =>
  contact.kind === 'email'
    ? contact.value.trim().toLowerCase()
    : contact.value.replace(/\D/g, '')

const blankToNull = (value: string): string | null =>
  value.trim() === '' ? null : value.trim()

export type PartyFormProps = {
  /** Absent means "create". */
  party?: PartySummary | undefined
  onClose: () => void
  /**
   * O cadastro recém-criado, antes de fechar (F174): o pedido que abriu este
   * formulário escolhe o cliente na hora, sem a pessoa ter de buscá-lo.
   */
  onCreated?: ((party: PartyResponse['party']) => void) | undefined
}

/**
 * Mounted only while the dialog is open, and keyed by the party being edited.
 * That is what makes the initial values correct without an effect resetting
 * them after the fact — see the call site in routes/pessoas.tsx.
 */
export const PartyForm = ({
  party,
  onClose,
  onCreated,
}: PartyFormProps): ReactNode => {
  const queryClient = useQueryClient()
  const { allows } = useAuth()
  /*
   * O vendedor do cliente (F210). A lista vem de `/team/salespeople`, que é de
   * quem vende; sem ela, o campo nem aparece e o cadastro não mexe na carteira.
   */
  const canPickSalesperson = allows('sales.write')
  const salespeople = useQuery({
    queryKey: ['salespeople'],
    queryFn: async ({ signal }) => await fetchSalespeople(signal),
    enabled: canPickSalesperson,
  })
  const [formError, setFormError] = useState<string | null>(null)
  const editing = party !== undefined

  /*
   * Fechar com coisa digitada pergunta antes (F248): Esc, o X, o clique fora
   * e o Cancelar fechavam calados, e o cadastro digitado sumia.
   */
  const [discarding, setDiscarding] = useState(false)

  /*
   * O código do cliente (F211). Nulo é "não mexi": o formulário mostra o
   * próximo da loja e o servidor o reserva ao salvar. Digitado, vai o digitado.
   */
  const nextCode = useQuery({
    queryKey: ['parties', 'next-code'],
    queryFn: async ({ signal }) => await fetchNextPartyCode(signal),
    enabled: !editing,
  })
  const [typedCode, setTypedCode] = useState<string | null>(
    party?.code === undefined || party.code === null ? null : String(party.code)
  )
  const shownCode =
    typedCode ?? (nextCode.data === undefined ? '' : String(nextCode.data))
  const codeError =
    typedCode !== null && !/^[0-9]{1,15}$/.test(typedCode.trim())
      ? 'Só números'
      : typedCode !== null && Number(typedCode) === 0
        ? 'Maior que zero'
        : null

  /** O WhatsApp do cadastro novo, em cima (F211). Vira o contato principal. */
  const [whatsapp, setWhatsapp] = useState('')

  /*
   * "Mais dados" (F211): fechado no cadastro novo; aberto na edição de quem já
   * tem algo lá dentro, para ninguém achar que o CNPJ sumiu.
   */
  const [moreOpen, setMoreOpen] = useState(
    party !== undefined &&
      [
        party.document,
        party.stateRegistration,
        party.municipalRegistration,
        party.notes,
      ].some((value) => value !== null && value !== '')
  )

  /**
   * The concurrency token this form will send back.
   *
   * Starts as the version the list row carried, and moves when the addresses
   * and contacts section changes the party underneath — every one of those
   * writes bumps `party.version`, because they are changes to the same
   * aggregate. Without this the person who just added an address would be told
   * somebody else had edited the record: themselves.
   */
  const [version, setVersion] = useState(party?.version ?? 0)

  const onVersionChange = useCallback((next: number) => {
    setVersion(next)
  }, [])

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    trigger,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: party === undefined ? emptyValues : valuesFrom(party),
  })

  /**
   * The kind, mirrored into local state rather than read with `watch()`.
   *
   * Three labels depend on it — CPF/CNPJ, "Nome completo"/"Razão social",
   * "Apelido"/"Nome fantasia" — so the render has to follow the field. React
   * Hook Form's `watch()` returns a function the React Compiler cannot memoise,
   * which opts this whole component out of compilation; a piece of state kept
   * in step by the field's own `onChange` costs one line and keeps it in.
   */
  const [kind, setKind] = useState<FormValues['kind']>(
    party?.kind ?? emptyValues.kind
  )

  const kindField = register('kind', {
    onChange: (event: { target: { value: string } }) => {
      setKind(event.target.value as FormValues['kind'])
    },
  })

  /* O endereço e os contatos do cadastro NOVO (F130). Na edição, a ficha
     continua cuidando deles, com a versão da pessoa. */
  const [address, setAddress] = useState<AddressValues>({
    ...EMPTY_ADDRESS,
    kind: 'billing',
    isMain: true,
  })
  const [contacts, setContacts] = useState<readonly ContactDraft[]>([])
  const nextContactKey = useRef(0)

  const [lookup, setLookup] = useState<Lookup>({ state: 'idle' })
  /** O que a última busca preencheu — o que a próxima pode sobrescrever. */
  const lastCompany = useRef<CnpjCompany | null>(null)

  const offersLookup = !editing && kind === 'company'

  const searchCnpj = (raw: string, requested: boolean): void => {
    const document = normalizeDocument('company', raw)
    if (!isValidDocument('company', document)) {
      setLookup({ state: 'idle' })
      // Quem clicou em Buscar com um CNPJ errado precisa ver POR QUE nada veio.
      if (requested) void trigger('document')
      return
    }
    // O mesmo CNPJ já buscado não busca de novo a cada tecla — só a pedido.
    if (!requested && lastCompany.current?.document === document) return

    setLookup({ state: 'busy' })
    void lookupCnpj(document).then((company) => {
      if (company === null) {
        setLookup({ state: 'missed' })
        return
      }

      const previous = lastCompany.current
      setValue(
        'legalName',
        fillableWith(
          getValues('legalName'),
          company.legalName,
          previous?.legalName ?? null
        ),
        { shouldValidate: true }
      )
      setValue(
        'tradeName',
        fillableWith(
          getValues('tradeName'),
          company.tradeName,
          previous?.tradeName ?? null
        )
      )

      const found = company.address
      if (found !== null) {
        setAddress((current) => {
          const merged = mergeCnpjAddress(
            {
              cep: current.cep,
              street: current.street,
              number: current.number,
              complement: current.complement,
              district: current.district,
              city: current.city,
              uf: current.uf,
              ibgeCode: current.ibgeCode,
            },
            found,
            previous?.address ?? null
          )
          return {
            ...current,
            ...merged,
            uf: ufOrNull(merged.uf) ?? current.uf,
          }
        })
      }

      const additions: ContactDraft[] = [
        ...company.phones.map((phone) => ({
          kind: phoneKindOf(phone),
          value: phone,
        })),
        ...company.emails.map((email) => ({
          kind: 'email' as const,
          value: email,
        })),
      ].map((contact) => {
        nextContactKey.current += 1
        return { ...contact, key: nextContactKey.current }
      })
      setContacts((current) => {
        const known = new Set(current.map(contactKey))
        return [
          ...current,
          ...additions.filter((contact) => !known.has(contactKey(contact))),
        ]
      })

      lastCompany.current = company
      setLookup({ state: 'found', company })
    })
  }

  const documentField = register('document')

  const addressTouched = [
    address.cep,
    address.street,
    address.number,
    address.complement,
    address.district,
    address.city,
    address.ibgeCode,
  ].some((value) => value.trim() !== '')

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      // Empty means "no value", never an empty string in the database.
      const payload = {
        kind: values.kind,
        legalName: values.legalName,
        tradeName: values.tradeName.length === 0 ? null : values.tradeName,
        // Normalised here too, so the value the server stores is the value the
        // browser validated. The server normalises again regardless.
        document:
          values.document.length === 0
            ? null
            : normalizeDocument(values.kind, values.document),
        stateRegistration:
          values.stateRegistration.length === 0
            ? null
            : values.stateRegistration,
        stateRegistrationStatus: values.stateRegistrationStatus,
        municipalRegistration:
          values.municipalRegistration.length === 0
            ? null
            : values.municipalRegistration,
        notes: values.notes.length === 0 ? null : values.notes,
        // Só quem vê a lista de vendedores mexe na carteira (F210).
        ...(canPickSalesperson
          ? {
              salespersonUserId:
                values.salespersonUserId === ''
                  ? null
                  : values.salespersonUserId,
            }
          : {}),
        roles: values.roles,
        // F211: só o que a pessoa digitou; senão o servidor dá o próximo.
        ...(typedCode === null || typedCode.trim() === ''
          ? {}
          : { code: Number(typedCode.trim()) }),
      }

      if (party !== undefined) {
        return await updateParty(party.id, {
          ...payload,
          // The freshest version this form knows about. If somebody else saved
          // in the meantime the server refuses rather than overwriting them.
          version,
        })
      }

      return await createParty({
        ...payload,
        ...(addressTouched
          ? {
              address: {
                kind: address.kind,
                cep: blankToNull(address.cep),
                street: address.street.trim(),
                number: blankToNull(address.number),
                complement: blankToNull(address.complement),
                district: blankToNull(address.district),
                city: address.city.trim(),
                ibgeCode: blankToNull(address.ibgeCode),
                uf: address.uf,
                country: address.country,
                isMain: true,
              },
            }
          : {}),
        contacts: [
          // O WhatsApp de cima primeiro: é ele que vira o principal.
          ...(whatsapp.trim() === ''
            ? []
            : [{ kind: 'whatsapp' as const, value: whatsapp.trim() }]),
          ...contacts,
        ]
          .filter((contact) => contact.value.trim() !== '')
          .map((contact) => ({
            kind: contact.kind,
            value: contact.value.trim(),
            label: null,
            isMain: false,
          })),
      })
    },
    onSuccess: async (response) => {
      // O retorno à vista, com o código que o cliente ganhou (F248).
      const saved = response.party
      const code = saved.code == null ? '' : ` ${String(saved.code)}`
      notify.success(
        editing
          ? `Cadastro de ${partyDisplayName(saved)} salvo.`
          : `Cadastro${code} criado: ${partyDisplayName(saved)}.`
      )
      await queryClient.invalidateQueries({ queryKey: ['parties'] })
      if (!editing) onCreated?.(response.party)
      onClose()
    },
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError
          ? error.message
          : 'Não foi possível salvar. Tente novamente.'
      )
    },
  })

  const onSubmit = handleSubmit(
    (values) => {
      // Um envio por vez (F248): o segundo, com o primeiro andando, criaria outro.
      if (mutation.isPending) return
      setFormError(null)
      if (codeError !== null) return
      /*
       * Endereço começado e não terminado. O servidor recusaria sem logradouro ou
       * município, e a pessoa leria um erro sobre um campo que nem lembra ter
       * tocado; aqui ela lê qual falta, ou apaga.
       */
      if (
        !editing &&
        addressTouched &&
        (address.street.trim() === '' || address.city.trim() === '')
      ) {
        setFormError(
          'Complete o logradouro e o município do endereço, ou apague o que foi digitado nele.'
        )
        return
      }
      mutation.mutate(values)
    },
    // Um erro num campo recolhido não pode ficar invisível: abre "Mais dados".
    (invalid) => {
      if (
        invalid.document !== undefined ||
        invalid.roles !== undefined ||
        invalid.stateRegistration !== undefined ||
        invalid.municipalRegistration !== undefined ||
        invalid.notes !== undefined
      ) {
        setMoreOpen(true)
      }
    }
  )

  const requestClose = (): void => {
    if ((isDirty || addressTouched) && !mutation.isPending) {
      setDiscarding(true)
      return
    }
    onClose()
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) requestClose()
      }}
    >
      <DialogContent
        title={editing ? 'Editar cadastro' : 'Novo cadastro'}
        description={
          editing
            ? partyDisplayName(party)
            : 'Um cadastro só, com os papéis que a pessoa tiver.'
        }
        // Qual cliente é, à vista em todas as abas (F248).
        meta={editing ? <PartyIdentity party={party} /> : undefined}
        className="max-w-2xl"
      >
        <form
          onSubmit={(event) => {
            void onSubmit(event)
          }}
          noValidate
          className="flex flex-col gap-4"
        >
          {formError === null ? null : (
            <p
              role="alert"
              className="rounded-(--radius-md) border border-(--danger) bg-(--danger-subtle) px-3 py-2 text-(length:--text-xs) text-(--danger)"
            >
              {formError}
            </p>
          )}

          <div className="grid gap-4 sm:grid-cols-[10rem_minmax(0,1fr)]">
            <Field
              label="Código"
              error={codeError ?? undefined}
              hint={
                editing ? 'Único na loja' : 'Gerado ao salvar — dá para trocar'
              }
            >
              {(field) => (
                <Input
                  {...field}
                  value={shownCode}
                  onChange={(event) => {
                    setTypedCode(event.target.value)
                  }}
                  inputMode="numeric"
                  className="font-(--font-mono)"
                />
              )}
            </Field>
            <Field label="Tipo" required error={errors.kind?.message}>
              {(field) => (
                <Select {...field} {...kindField}>
                  <option value="company">Pessoa jurídica</option>
                  <option value="person">Pessoa física</option>
                </Select>
              )}
            </Field>
          </div>

          <Field
            label={kind === 'person' ? 'Nome completo' : 'Razão social'}
            required
            error={errors.legalName?.message}
          >
            {(field) => (
              <Input
                {...field}
                {...register('legalName')}
                autoFocus
                placeholder={
                  kind === 'person'
                    ? 'Maria Aparecida de Souza'
                    : 'Elite Digital Comércio de Eletrônicos Ltda'
                }
              />
            )}
          </Field>

          <Field
            label={kind === 'person' ? 'Apelido' : 'Nome fantasia'}
            error={errors.tradeName?.message}
          >
            {(field) => (
              <Input
                {...field}
                {...register('tradeName')}
                placeholder={
                  kind === 'person'
                    ? 'Como o balcão chama — ex.: Zé da Cell'
                    : 'Elite Digital'
                }
              />
            )}
          </Field>

          {editing ? null : (
            <Field
              label="WhatsApp"
              hint="Vira o contato principal. Outros contatos em Mais dados."
            >
              {(field) => (
                <Input
                  {...field}
                  value={whatsapp}
                  onChange={(event) => {
                    setWhatsapp(event.target.value)
                  }}
                  inputMode="tel"
                  placeholder="(33) 98765-4321"
                />
              )}
            </Field>
          )}

          {canPickSalesperson ? (
            <Field
              label="Vendedor responsável"
              hint="A carteira: a conversa de WhatsApp deste cliente cai para essa pessoa quando o número não tem dono."
            >
              {(field) => (
                <Select {...field} {...register('salespersonUserId')}>
                  <option value="">Sem vendedor</option>
                  {(salespeople.data?.items ?? []).map((person) => (
                    <option key={person.userId} value={person.userId}>
                      {person.displayName}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          ) : null}

          {/*
            Mais dados (F211): o que o balcão raramente tem na hora. Recolhido,
            e não escondido — um clique, e o erro de um campo daqui abre sozinho.
          */}
          <details
            open={moreOpen}
            onToggle={(event) => {
              setMoreOpen(event.currentTarget.open)
            }}
            className="rounded-(--radius-md) border border-(--border-subtle) px-3 py-2"
          >
            <summary className="cursor-pointer text-(length:--text-sm) font-(--weight-medium) text-(--text-primary)">
              Mais dados — CPF/CNPJ, e-mail, endereço, inscrição, papéis
            </summary>
            <div className="mt-3 flex flex-col gap-4">
              <Field
                label={kind === 'person' ? 'CPF' : 'CNPJ'}
                error={errors.document?.message}
                hint={
                  offersLookup
                    ? 'Só se o cliente tiver. Com o CNPJ completo, os dados vêm da Receita'
                    : 'Só se o cliente tiver. Pode ser digitado com pontuação'
                }
              >
                {(field) => (
                  <div className="flex gap-2">
                    <Input
                      {...field}
                      {...documentField}
                      onChange={(event) => {
                        void documentField.onChange(event)
                        // Ao completar um CNPJ válido, sem clique: é o que o dono
                        // pediu. Um CNPJ pela metade não consulta nada.
                        if (offersLookup) searchCnpj(event.target.value, false)
                      }}
                      /*
                        Deliberately NOT inputMode="numeric": a CNPJ issued from
                        July 2026 contains letters, and a numeric keypad would make
                        one impossible to type on a tablet. Master Specification
                        section 11 names "input HTML numeric" as forbidden.
                      */
                      placeholder={
                        kind === 'person'
                          ? '111.444.777-35'
                          : '12.ABC.345/01DE-35'
                      }
                      className="font-(--font-mono)"
                    />
                    {offersLookup ? (
                      <Button
                        type="button"
                        variant="secondary"
                        aria-label="Buscar os dados do CNPJ na Receita"
                        loading={lookup.state === 'busy'}
                        leadingIcon={
                          <Search aria-hidden="true" className="h-4 w-4" />
                        }
                        onClick={() => {
                          searchCnpj(getValues('document'), true)
                        }}
                      >
                        Buscar
                      </Button>
                    ) : null}
                  </div>
                )}
              </Field>

              {/*
              O resultado da busca, sem `role="alert"`: "não achei" NÃO é erro —
              o cadastro continua digitável, como era antes da busca existir. O
              aviso de empresa baixada é o único que pede atenção.
            */}
              {!offersLookup || lookup.state === 'idle' ? null : (
                <div
                  aria-live="polite"
                  className="rounded-(--radius-md) bg-(--surface-sunken) px-3 py-2 text-(length:--text-xs) text-(--text-secondary)"
                >
                  {lookup.state === 'busy' ? (
                    'Buscando os dados deste CNPJ na Receita…'
                  ) : lookup.state === 'missed' ? (
                    'Não achei os dados deste CNPJ, ou a consulta está fora do ar. Preencha à mão — dá no mesmo.'
                  ) : (
                    <>
                      <p>
                        Preenchido com os dados da Receita. Confira e edite o
                        que precisar — a inscrição estadual não vem na consulta.
                      </p>
                      {lookup.company.active ? null : (
                        <p className="mt-1 font-(--weight-semibold) text-(--danger)">
                          Atenção: na Receita este CNPJ está com situação “
                          {lookup.company.status === ''
                            ? 'desconhecida'
                            : lookup.company.status}
                          ”.
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}

              {/*
              The roles. Checkboxes rather than a "tipo de cadastro" dropdown,
              because several at once is the normal case and a dropdown would
              force the duplicate registration this whole module exists to avoid.
            */}
              <fieldset className="flex flex-col gap-2">
                <legend className="text-(length:--text-sm) font-(--weight-medium) text-(--text-primary)">
                  Papéis
                </legend>
                {/*
                A frase carrega a distinção que a palavra sozinha não carrega
                (F098). Entregador e Transportadora parecem sinônimos e não são.
              */}
                <p className="text-(length:--text-xs) text-(--text-muted)">
                  Uma empresa pode ter vários ao mesmo tempo. Entregador é quem
                  leva e volta com o dinheiro; Transportadora é quem leva a
                  mercadoria com a nota.
                </p>
                <div className="flex flex-wrap gap-4">
                  {PARTY_ROLE_VALUES.map((role) => (
                    <label
                      key={role}
                      className="flex items-center gap-2 text-(length:--text-sm) text-(--text-primary)"
                    >
                      <input
                        type="checkbox"
                        value={role}
                        {...register('roles')}
                        className="h-4 w-4 rounded-(--radius-sm) border-(--border-default) accent-(--brand-primary)"
                      />
                      {PARTY_ROLE_LABELS[role]}
                    </label>
                  ))}
                </div>
                {errors.roles?.message === undefined ? null : (
                  <p
                    role="alert"
                    className="text-(length:--text-xs) text-(--danger)"
                  >
                    {errors.roles.message}
                  </p>
                )}
              </fieldset>

              <div className="grid gap-4 sm:grid-cols-3">
                <Field
                  label="Situação da IE"
                  error={errors.stateRegistrationStatus?.message}
                >
                  {(field) => (
                    <Select {...field} {...register('stateRegistrationStatus')}>
                      {STATE_REGISTRATION_STATUS_VALUES.map((status) => (
                        <option key={status} value={status}>
                          {IE_STATUS_LABELS[status]}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>

                <Field
                  label="Inscrição estadual"
                  error={errors.stateRegistration?.message}
                  hint="Somente números"
                >
                  {(field) => (
                    <Input
                      {...field}
                      {...register('stateRegistration')}
                      className="font-(--font-mono)"
                    />
                  )}
                </Field>

                <Field
                  label="Inscrição municipal"
                  error={errors.municipalRegistration?.message}
                >
                  {(field) => (
                    <Input
                      {...field}
                      {...register('municipalRegistration')}
                      className="font-(--font-mono)"
                    />
                  )}
                </Field>
              </div>

              {editing ? null : (
                <>
                  <fieldset className="flex flex-col gap-2">
                    <legend className="text-(length:--text-sm) font-(--weight-medium) text-(--text-primary)">
                      Endereço
                    </legend>
                    <p className="text-(length:--text-xs) text-(--text-muted)">
                      Opcional. O CEP busca o endereço; com o CNPJ, ele vem da
                      Receita. Entra como o endereço principal.
                    </p>
                    <AddressInputs values={address} onChange={setAddress} />
                  </fieldset>

                  <fieldset className="flex flex-col gap-2">
                    <legend className="text-(length:--text-sm) font-(--weight-medium) text-(--text-primary)">
                      Contatos
                    </legend>
                    <p className="text-(length:--text-xs) text-(--text-muted)">
                      Opcional. O primeiro da lista vira o contato principal.
                    </p>
                    {contacts.map((contact, index) => (
                      <div
                        key={contact.key}
                        className="grid grid-cols-[9rem_minmax(0,1fr)_auto] gap-2"
                      >
                        <Select
                          aria-label={`Tipo do contato ${String(index + 1)}`}
                          value={contact.kind}
                          onChange={(event) => {
                            const next = event.target
                              .value as ContactDraft['kind']
                            setContacts((current) =>
                              current.map((entry) =>
                                entry.key === contact.key
                                  ? { ...entry, kind: next }
                                  : entry
                              )
                            )
                          }}
                        >
                          {CONTACT_KIND_VALUES.map((value) => (
                            <option key={value} value={value}>
                              {CONTACT_KIND_LABELS[value]}
                            </option>
                          ))}
                        </Select>
                        <Input
                          aria-label={`Contato ${String(index + 1)}`}
                          value={contact.value}
                          placeholder={
                            contact.kind === 'email'
                              ? 'compras@empresa.com.br'
                              : '(11) 98765-4321'
                          }
                          onChange={(event) => {
                            const next = event.target.value
                            setContacts((current) =>
                              current.map((entry) =>
                                entry.key === contact.key
                                  ? { ...entry, value: next }
                                  : entry
                              )
                            )
                          }}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-label={`Remover o contato ${String(index + 1)}`}
                          leadingIcon={
                            <Trash2 aria-hidden="true" className="h-4 w-4" />
                          }
                          onClick={() => {
                            setContacts((current) =>
                              current.filter(
                                (entry) => entry.key !== contact.key
                              )
                            )
                          }}
                        />
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="self-start"
                      leadingIcon={
                        <Plus aria-hidden="true" className="h-4 w-4" />
                      }
                      onClick={() => {
                        nextContactKey.current += 1
                        const key = nextContactKey.current
                        setContacts((current) => [
                          ...current,
                          { key, kind: 'mobile', value: '' },
                        ])
                      }}
                    >
                      Adicionar contato
                    </Button>
                  </fieldset>
                </>
              )}

              <Field label="Observações" error={errors.notes?.message}>
                {(field) => <Textarea {...field} {...register('notes')} />}
              </Field>
            </div>
          </details>

          {party === undefined ? null : (
            <PartyDetails
              partyId={party.id}
              canWrite={allows('party.write')}
              onVersionChange={onVersionChange}
            />
          )}

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                Cancelar
              </Button>
            </DialogClose>
            <Button
              type="submit"
              variant="primary"
              loading={mutation.isPending}
            >
              {editing ? 'Salvar' : 'Cadastrar'}
            </Button>
          </DialogFooter>
        </form>

        <ConfirmDialog
          open={discarding}
          onOpenChange={(next) => {
            if (!next) setDiscarding(false)
          }}
          title="Descartar as alterações?"
          description={
            editing
              ? `O que você mudou no cadastro de ${partyDisplayName(party)} não foi salvo.`
              : 'O cadastro ainda não foi criado, e o que foi digitado se perde.'
          }
          confirmLabel="Descartar"
          cancelLabel="Continuar editando"
          onConfirm={onClose}
        />
      </DialogContent>
    </Dialog>
  )
}
